/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { safeLocalStorage } from "@/helper/safeLocalStorage";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import {
  CONSENT_KEY,
  PREFS_KEY,
  CURRENT_POLICY_VERSION,
  type ConsentRecord,
  readCookiePrefs,
} from "@/utils/cookiePrefs";
import { useFocusTrap } from "@/hooks/useFocusTrap";

/**
 * GDPR cookie consent banner with granular preferences.
 *
 * The preferences panel shows:
 *   - Essential cookies (always on)
 *   - Analytics toggle (Google Analytics; also Sentry's page-speed
 *     measurements and replays, src/utils/sentryConsent.ts)
 *   - Advertising toggle (Google Ads)
 * Each Google tag loads only after its own consent (index.html).
 *
 * On a ?standalone=true page (showBanner false) the banner does not open by
 * itself; the preferences panel still opens when asked.
 *
 * Banner view: Accept All | Manage Preferences | Decline
 * Preferences view: toggles + Save / Accept All / Decline All
 *
 * The footer "Cookie Settings" button dispatches "ds:show-cookie-preferences"
 * which re-opens in preferences mode without a page reload.
 *
 * Storage:
 *   ds_cookie_prefs    — JSON: ConsentRecord (analytics, timestamp, policyVersion, source)
 *   ds_cookie_consent  — legacy "accepted"/"declined" (backward compat write)
 *
 * GDPR compliance:
 *   - D-01: Stores timestamp + policy version for auditable consent records.
 *   - D-02: Sends gtag consent update to stop GA4 immediately when user withdraws.
 *
 * Accessibility:
 *   - The preferences panel is a true modal dialog (role="dialog", aria-modal="true").
 *   - Focus is trapped inside the panel while it is open (WCAG 2.1.2 Level A).
 *   - Escape key closes the panel and returns focus to the trigger element.
 *   - On open, focus moves to the first interactive element (analytics toggle).
 */

/**
 * H-2: the height contract between this banner and the support dock.
 *
 * The dock used to lift itself by a fixed number of pixels chosen to clear
 * "roughly" this banner. That number cannot be right on every screen, because
 * the banner's height follows its text and its text follows the language — a
 * two-line German banner and a one-line English banner are different heights
 * on the same phone. A bigger guess only trades an overlap in one language for
 * a floating gap in another.
 *
 * So the banner measures itself and publishes the answer on the root element;
 * techSupportChat.css reads it with a var() fallback. This name is the whole
 * contract and it is set in exactly one place in the front end — two writers
 * of one custom property would fight silently, with the last one to render
 * winning.
 */
export const COOKIE_BANNER_HEIGHT_PROPERTY = "--ds-cookie-banner-height";

type ConsentSource = ConsentRecord["source"];

function applyAndSavePrefs(
  analytics: boolean,
  advertising: boolean,
  source: ConsentSource,
): void {
  const record: ConsentRecord = {
    analytics,
    advertising,
    source,
    timestamp: new Date().toISOString(),
    policyVersion: CURRENT_POLICY_VERSION,
  };

  safeLocalStorage.setItem(PREFS_KEY, JSON.stringify(record));
  safeLocalStorage.setItem(CONSENT_KEY, analytics ? "accepted" : "declined");

  // Notify same-tab listeners (e.g. TechSupportChat banner offset).
  // The native `storage` event only fires in OTHER tabs; this custom event
  // lets same-tab components react when consent changes.
  window.dispatchEvent(new CustomEvent("ds:cookie-consent-saved"));

  // N-1: one consent update carrying BOTH decisions. Each signal reflects the
  // answer the visitor actually gave for that purpose. Granting the three
  // advertising signals because somebody agreed to analytics would record a
  // consent they never gave -- and since June 2026 ad_storage is the only
  // control over whether advertising data reaches Google Ads from a linked
  // GA4 property, so a wrongly granted signal has an immediate effect.
  window.gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: advertising ? "granted" : "denied",
    ad_user_data: advertising ? "granted" : "denied",
    ad_personalization: advertising ? "granted" : "denied",
  });

  // Session 515 review, SU-2: each tag loads only with its own consent, so
  // an advertising-only choice must load the Ads tag too (index.html loads
  // Analytics only when analytics is true, Ads only when advertising is).
  if (analytics || advertising) {
    window.__ds_load_analytics?.({ analytics, advertising });
  }
}

type View = "banner" | "preferences";

interface CookieConsentProps {
  /**
   * Whether the banner opens by itself when no choice is stored. False on a
   * ?standalone=true page (Session 515, the shop's refund-policy link and the
   * footer's privacy-policy window): that view is shown inside a purchase
   * window that starts every session with empty storage, has no footer, and
   * the notice sat over the policy being read. The preferences panel still
   * opens there when the visitor asks for it (the policy's own Cookie
   * Settings link), and no tag loads without a stored choice either way.
   */
  showBanner?: boolean;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ showBanner = true }) => {
  const { t } = useTranslation();
  const [view, setView] = useState<View | null>(null);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [advertisingOn, setAdvertisingOn] = useState(false);

  // Refs for focus trap: the preferences panel container and the element that
  // triggered the panel (so focus returns to it when the panel closes).
  const prefsRef = useRef<HTMLDivElement>(null);
  const manageButtonRef = useRef<HTMLButtonElement>(null);

  // H-2: the element whose height the support dock needs. Only the banner view
  // carries this ref. The preferences panel is a modal that can grow to 90vh,
  // and lifting the dock by *that* would push it off the top of a phone; while
  // the panel is open the property is removed and the dock falls back to the
  // value it used before this change.
  const bannerRef = useRef<HTMLDivElement>(null);

  // Activate focus trap only while the preferences panel is showing.
  // Escape closes the panel and returns focus to the trigger button.
  const handleClosePrefs = useCallback((): void => {
    setView(null);
  }, []);

  useFocusTrap(
    view === "preferences",
    prefsRef,
    handleClosePrefs,
    manageButtonRef,
  );

  useEffect(() => {
    if (showBanner && !readCookiePrefs()) {
      setView("banner");
    }
  }, [showBanner]);

  // H-2: publish the measured height, and take it away again on unmount.
  //
  // useLayoutEffect, not useEffect, so the dock is already in the right place
  // on the first paint rather than jumping there afterwards.
  //
  // Removing the property in the cleanup is what makes dismissal work: the
  // banner unmounts when the visitor accepts or declines (`view` becomes null
  // and this component returns null), the property goes with it, and the dock
  // returns to its normal position. There is no second mechanism to keep in
  // step. `view` is a dependency so that moving to the preferences panel — a
  // different element with a different height — retracts the value instead of
  // leaving a stale one behind.
  //
  // ResizeObserver is assumed present: every browser this site supports has
  // had it since 2020, and src/test/setup.ts stubs it for the suite.
  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el) return;

    const publish = (): void => {
      document.documentElement.style.setProperty(
        COOKIE_BANNER_HEIGHT_PROPERTY,
        `${el.offsetHeight}px`,
      );
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(
        COOKIE_BANNER_HEIGHT_PROPERTY,
      );
    };
  }, [view]);

  useEffect(() => {
    const handler = (): void => {
      const existing = readCookiePrefs();
      setAnalyticsOn(existing?.analytics ?? false);
      // Session 515: the panel shows the advertising answer actually stored.
      // It used to open with advertising off, so saving there to change only
      // analytics silently withdrew an advertising consent.
      setAdvertisingOn(existing?.advertising ?? false);
      setView("preferences");
    };
    window.addEventListener("ds:show-cookie-preferences", handler);
    return () =>
      window.removeEventListener("ds:show-cookie-preferences", handler);
  }, []);

  const handleAcceptAll = useCallback((): void => {
    applyAndSavePrefs(true, true, "banner_accept");
    setView(null);
  }, []);

  const handleDeclineAll = useCallback((): void => {
    applyAndSavePrefs(false, false, "banner_decline");
    setView(null);
  }, []);

  const handleSavePrefs = useCallback((): void => {
    applyAndSavePrefs(analyticsOn, advertisingOn, "settings_update");
    setView(null);
  }, [analyticsOn, advertisingOn]);

  const openPreferences = useCallback((): void => {
    const existing = readCookiePrefs();
    setAnalyticsOn(existing?.analytics ?? false);
    setAdvertisingOn(existing?.advertising ?? false);
    setView("preferences");
  }, []);

  if (!view) return null;

  // ── Preferences panel ────────────────────────────────────────────────────
  if (view === "preferences") {
    return (
      <div
        ref={prefsRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("cookie_consent.preferences_title")}
        className="cookie-consent-banner cookie-consent-banner--prefs"
      >
        <div className="cookie-consent-prefs-inner">
          <h2 className="cookie-consent-prefs-heading">
            {t("cookie_consent.preferences_title")}
          </h2>
          <p className="cookie-consent-prefs-intro">
            {t("cookie_consent.preferences_intro")}{" "}
            <Link
              to={AddLangToPath("/privacy-policy")}
              className="cookie-consent-link"
            >
              {t("cookie_consent.privacy_link")}
            </Link>
            {"."}
          </p>

          {/* Essential — always on */}
          <div className="cookie-pref-row">
            <div className="cookie-pref-info">
              <span className="cookie-pref-title">
                {t("cookie_consent.essential_title")}
              </span>
              <span className="cookie-pref-desc">
                {t("cookie_consent.essential_description")}
              </span>
            </div>
            <span className="cookie-pref-always-on">
              {t("cookie_consent.always_on")}
            </span>
          </div>

          {/* Analytics — Google Analytics */}
          <div className="cookie-pref-row">
            <div className="cookie-pref-info">
              <span className="cookie-pref-title">
                {t("cookie_consent.analytics_title")}
              </span>
              <span className="cookie-pref-desc">
                {t("cookie_consent.analytics_description")}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={analyticsOn}
              className={`cookie-pref-toggle${analyticsOn ? " cookie-pref-toggle--on" : ""}`}
              onClick={() => setAnalyticsOn((prev) => !prev)}
              aria-label={t("cookie_consent.analytics_title")}
            >
              <span className="cookie-pref-toggle-thumb" />
            </button>
          </div>

          {/* Advertising — Google Ads. N-1: a separate purpose needs a
              separate question. Advertising storage, advertising data
              transmission and ad personalisation are three purposes distinct
              from analytics, and GDPR requires consent to be specific. */}
          <div className="cookie-pref-row">
            <div className="cookie-pref-info">
              <span className="cookie-pref-title">
                {t("cookie_consent.advertising_title")}
              </span>
              <span className="cookie-pref-desc">
                {t("cookie_consent.advertising_description")}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={advertisingOn}
              className={`cookie-pref-toggle${advertisingOn ? " cookie-pref-toggle--on" : ""}`}
              onClick={() => setAdvertisingOn((prev) => !prev)}
              aria-label={t("cookie_consent.advertising_title")}
            >
              <span className="cookie-pref-toggle-thumb" />
            </button>
          </div>

          <div className="cookie-consent-prefs-actions">
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn--accept"
              onClick={handleAcceptAll}
            >
              {t("cookie_consent.accept_all")}
            </button>
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn--save"
              onClick={handleSavePrefs}
            >
              {t("cookie_consent.save_preferences")}
            </button>
            <button
              type="button"
              className="cookie-consent-btn cookie-consent-btn--decline"
              onClick={handleDeclineAll}
            >
              {t("cookie_consent.decline_all")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main banner ──────────────────────────────────────────────────────────
  return (
    <div
      ref={bannerRef}
      role="region"
      aria-label={t("cookie_consent.title")}
      className="cookie-consent-banner"
    >
      <div className="cookie-consent-content">
        <p className="cookie-consent-text">
          <strong>{t("cookie_consent.title")}.</strong>{" "}
          {t("cookie_consent.body")}{" "}
          <Link
            to={AddLangToPath("/privacy-policy")}
            className="cookie-consent-link"
          >
            {t("cookie_consent.privacy_link")}
          </Link>
          {"."}
        </p>
        <div className="cookie-consent-actions">
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-btn--accept"
            onClick={handleAcceptAll}
          >
            {t("cookie_consent.accept_all")}
          </button>
          <button
            ref={manageButtonRef}
            type="button"
            className="cookie-consent-btn cookie-consent-btn--manage"
            onClick={openPreferences}
          >
            {t("cookie_consent.manage_preferences")}
          </button>
          <button
            type="button"
            className="cookie-consent-btn cookie-consent-btn--decline"
            onClick={handleDeclineAll}
          >
            {t("cookie_consent.decline_all")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
